import { IS_PRODUCTION_DEPLOY } from '@/lib/deploy-env';

// Client error + monitoring is sent to Better Stack via their JS tag (loaded
// async from betterstack.net), gated on Concord's `analytics` consent. This is
// Better Stack's current (beta) frontend integration — the same approach as
// concord-marketing's logspot-web/concord-web (the backend, by contrast, uses
// the Sentry SDK). Feature toggles (session replay, console logs, website
// analytics) live in the Better Stack dashboard's Frontend tab — keep this
// errors-only there.
//
// Fail-closed: outside production, without a source token, or until analytics
// consent is granted, nothing loads and no errors are sent. Source maps are
// uploaded to Better Stack by @sentry/vite-plugin (see vite.config.ts) under the
// same `release` (the build id) so the minified browser stack traces
// symbolicate. https://betterstack.com/docs/errors/js-tag/install/

function initBetterStackTag(): void {
  if (typeof window === 'undefined' || window._betterstackInitialized) return;
  if (!IS_PRODUCTION_DEPLOY) return;

  const token = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN;
  if (!token) return;

  window._betterstackInitialized = true;

  // Command stub that queues calls until b.js loads and drains the queue.
  const queue: unknown[][] = [];
  const bs: BetterStack = (...args: unknown[]) => {
    queue.push(args);
  };
  bs.q = queue;
  bs.l = Date.now();
  window.betterstack = bs;

  const script = document.createElement('script');
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://betterstack.net/b.js?t=${token}`;
  document.head.appendChild(script);

  // Release matches the source maps uploaded at build time (vite.config.ts), so
  // minified stack traces symbolicate. Automated from the build id — no env var.
  bs('init', {
    environment: import.meta.env.VITE_DEPLOY_ENV,
    release: __APP_BUILD__.display,
  });
}

// Register the consent gate. Mirrors concord-marketing: hook Concord's analytics
// consent now if the API is already present, else on the `concord-ready` event.
// onConsent fires immediately when already consented, so nothing is missed once
// consent exists; errors before consent are intentionally never captured.
export function initBetterStack(): void {
  if (typeof window === 'undefined') return;

  if (window.concord?.consent?.onConsent) {
    window.concord.consent.onConsent('analytics', initBetterStackTag);
  } else {
    window.addEventListener(
      'concord-ready',
      () =>
        window.concord?.consent?.onConsent?.('analytics', initBetterStackTag),
      { once: true },
    );
  }
}
